import React, { useState, useRef } from 'react';
import { Bell, Edit } from 'lucide-react';
import { useI18n } from '../../utils/i18n';
import apiService from '../../services/apiService';
import { useToast } from '../ToastProvider';

// #312: Reiter „Benachrichtigungen“ (Hinweisleiste und Release-Note) des
// Admin-Bereichs, Code unverändert aus AdminPanel.js übernommen (Muster und
// Begründung: admin/LogsTab.js).
//
// Die beiden Speicher-Timer (`saveNotifTimerRef`, `saveReleaseNoteTimerRef`)
// speichern eine Eingabe 400 ms verzögert zum Server und werden BEWUSST nicht
// beim Verlassen abgeräumt: Wer tippt und den Bereich sofort verlässt, soll
// seine Änderung nicht still verlieren (#307). Weil das Panel diesen Hook
// aufruft, leben sie wie bisher so lange wie das Panel, nicht wie der Reiter.
export function useNotificationsTab({ setError }) {
  const { t } = useI18n();
  const { pushToast } = useToast();
  // Notification State
  const [notificationConfig, setNotificationConfig] = useState({ isActive: false, message: '' });
  const saveNotifTimerRef = useRef(null);
  const [savingNotification, setSavingNotification] = useState(false);

  // Release Note State
  const [releaseNote, setReleaseNote] = useState({ title: '', content: '', isActive: false });
  const saveReleaseNoteTimerRef = useRef(null);
  const [savingReleaseNote, setSavingReleaseNote] = useState(false);
  // #195: importierte Versionen (release_note_versions) — nur Anzeige
  const [releaseNoteVersions, setReleaseNoteVersions] = useState([]);

  const scheduleSaveNotification = (nextConfig, delayMs = 400) => {
    try { if (saveNotifTimerRef.current) clearTimeout(saveNotifTimerRef.current); } catch (_) {}
    saveNotifTimerRef.current = setTimeout(async () => {
      setSavingNotification(true);
      setError(null);
      try {
        const resp = await apiService.updateNotificationConfig(nextConfig);
        // Set to server-confirmed config (avoid immediate stale read)
        setNotificationConfig(resp.config || nextConfig);
        pushToast({ message: t('admin.notifications.saved', 'Benachrichtigung gespeichert.'), type: 'success' });
      } catch (error) {
        console.error('Error saving notification (debounced):', error);
        setError(t('admin.notifications.saveError', 'Fehler beim Speichern der Benachrichtigung.'));
      } finally {
        setSavingNotification(false);
      }
    }, delayMs);
  };

  const scheduleSaveReleaseNote = (nextNote, delayMs = 400) => {
    try { if (saveReleaseNoteTimerRef.current) clearTimeout(saveReleaseNoteTimerRef.current); } catch (_) {}
    saveReleaseNoteTimerRef.current = setTimeout(async () => {
      setSavingReleaseNote(true);
      setError(null);
      try {
        const resp = await apiService.updateReleaseNoteAdmin(nextNote);
        setReleaseNote(resp.note || nextNote);
        pushToast({ message: t('admin.releaseNote.saved', 'Releasenote gespeichert.'), type: 'success' });
      } catch (error) {
        console.error('Error saving release note (debounced):', error);
        setError(t('admin.releaseNote.saveError', 'Fehler beim Speichern der Releasenote.'));
      } finally {
        setSavingReleaseNote(false);
      }
    }, delayMs);
  };

  return { notificationConfig, setNotificationConfig, saveNotifTimerRef, savingNotification, setSavingNotification, releaseNote, setReleaseNote, saveReleaseNoteTimerRef, savingReleaseNote, setSavingReleaseNote, releaseNoteVersions, setReleaseNoteVersions, scheduleSaveNotification, scheduleSaveReleaseNote };
}

const NotificationsTab = ({ tab }) => {
  const { t } = useI18n();
  const { notificationConfig, setNotificationConfig, savingNotification, releaseNote, setReleaseNote, savingReleaseNote, releaseNoteVersions, scheduleSaveNotification, scheduleSaveReleaseNote } = tab;

  return (
    <div className="admin-section">
      <h3>{t('admin.notifications.heading', 'Benachrichtigungen')}</h3>
      <p className="section-description">
        {t('admin.notifications.desc', 'Steuern Sie eine globale Hinweisleiste ganz oben auf der Webseite. Diese erscheint in Orange mit dem folgenden Text.')}
      </p>

      <div className="settings-group">
        <label className="settings-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Bell size={16} /> {t('admin.notifications.barLabel', 'Globale Hinweisleiste')}
        </label>

        <div className="form-row">
          <div className="form-group" style={{ display: 'flex', alignItems: 'baseline', gap: 12, justifyContent: 'flex-start', paddingTop: 30 }}>
            <label style={{ minWidth: 140 }}>{t('admin.notifications.enabled', 'Aktiviert')}</label>
            <button
              type="button"
              onClick={() => {
                const next = { ...notificationConfig, isActive: !notificationConfig.isActive };
                setNotificationConfig(next);
                // schneller speichern beim Toggle
                scheduleSaveNotification(next, 200);
              }}
              disabled={savingNotification}
              aria-pressed={!!notificationConfig.isActive}
              className="btn"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                borderRadius: 20,
                border: '1px solid',
                borderColor: notificationConfig.isActive ? 'var(--color-success)' : 'var(--color-border-input)',
                background: notificationConfig.isActive ? 'linear-gradient(180deg, var(--color-success-bright) 0%, var(--color-success-bright-hover) 100%)' : 'var(--color-bg)',
                color: notificationConfig.isActive ? 'var(--color-text-inverse)' : 'var(--color-text)'
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: notificationConfig.isActive ? 'var(--color-surface)' : 'var(--color-surface-faint)',
                  boxShadow: notificationConfig.isActive ? '0 0 0 2px var(--color-glass-60)' : 'none'
                }}
              />
              {notificationConfig.isActive ? t('common.active', 'Aktiv') : t('common.inactive', 'Inaktiv')}
            </button>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>{t('admin.notifications.message', 'Nachricht')}</label>
            <input
              type="text"
              value={notificationConfig.message || ''}
              onChange={(e) => {
                const msg = e.target.value;
                const next = { ...notificationConfig, message: msg };
                // Beim Bearbeiten: automatisch deaktivieren
                if (next.isActive) next.isActive = false;
                setNotificationConfig(next);
                // 3s Inaktivität abwarten, bevor gespeichert und Erfolg gemeldet wird
                scheduleSaveNotification(next, 3000);
              }}
              placeholder={t('admin.notifications.placeholder', 'Text für die Hinweisleiste...')}
              
            />
          </div>
        </div>

      </div>

      <div className="settings-group" style={{ marginTop: 32 }}>
        <label className="settings-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Edit size={16} /> {t('admin.releaseNote.heading', 'Hinweis bei Login (einmalig)')}
        </label>
        <p className="section-description">
          {t('admin.releaseNote.desc', 'Steuert ausschließlich den Einmal-Hinweis, den jeder Benutzer nach dem Login einmal als Overlay sieht. Die Versions-Historie im „Was ist neu?"-Modal kommt dagegen aus dem CHANGELOG-Import beim Deploy und wird hier nicht gepflegt.')}
        </p>

        <div className="form-row">
          <div className="form-group" style={{ display: 'flex', alignItems: 'baseline', gap: 12, justifyContent: 'flex-start', paddingTop: 30 }}>
            <label style={{ minWidth: 140 }}>{t('admin.releaseNote.enabled', 'Aktiviert')}</label>
            <button
              type="button"
              onClick={() => {
                const next = { ...releaseNote, isActive: !releaseNote.isActive };
                setReleaseNote(next);
                scheduleSaveReleaseNote(next, 200);
              }}
              disabled={savingReleaseNote}
              aria-pressed={!!releaseNote.isActive}
              className="btn"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                borderRadius: 20,
                border: '1px solid',
                borderColor: releaseNote.isActive ? 'var(--color-success)' : 'var(--color-border-input)',
                background: releaseNote.isActive ? 'linear-gradient(180deg, var(--color-success-bright) 0%, var(--color-success-bright-hover) 100%)' : 'var(--color-bg)',
                color: releaseNote.isActive ? 'var(--color-text-inverse)' : 'var(--color-text)'
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: releaseNote.isActive ? 'var(--color-surface)' : 'var(--color-surface-faint)',
                  boxShadow: releaseNote.isActive ? '0 0 0 2px var(--color-glass-60)' : 'none'
                }}
              />
              {releaseNote.isActive ? t('common.active', 'Aktiv') : t('common.inactive', 'Inaktiv')}
            </button>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>{t('admin.releaseNote.title', 'Titel')}</label>
            <input
              type="text"
              value={releaseNote.title || ''}
              onChange={(e) => {
                const next = { ...releaseNote, title: e.target.value };
                setReleaseNote(next);
                scheduleSaveReleaseNote(next, 1500);
              }}
              placeholder={t('admin.releaseNote.titlePlaceholder', 'z. B. Update v2.5.0')}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>{t('admin.releaseNote.content', 'Inhalt')}</label>
            <textarea
              value={releaseNote.content || ''}
              onChange={(e) => {
                const next = { ...releaseNote, content: e.target.value };
                setReleaseNote(next);
                scheduleSaveReleaseNote(next, 1500);
              }}
              placeholder={t('admin.releaseNote.contentPlaceholder', 'Neue Funktionen, Änderungen, Bugfixes...')}
              rows={8}
              style={{ width: '100%', resize: 'vertical', minHeight: 120 }}
            />
          </div>
        </div>

        {/* #195: Versions-Historie nur zur Orientierung — Quelle ist der
            CHANGELOG-Import beim Deploy, hier bewusst nicht pflegbar */}
        {releaseNoteVersions.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <label className="settings-label">{t('admin.releaseNote.versionsHeading', 'Versions-Historie („Was ist neu?")')}</label>
            <p className="section-description">
              {t('admin.releaseNote.versionsHint', 'Wird beim Deploy aus dem CHANGELOG importiert und ist hier nur zur Ansicht — gepflegt wird sie nicht im Admin-Bereich.')}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {releaseNoteVersions.map(v => (
                <span key={v.version} style={{ background: 'var(--color-surface-muted)', border: '1px solid var(--color-border)', borderRadius: 12, padding: '2px 10px', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  v{v.version}
                  {v.releasedAt && (
                    <span style={{ color: 'var(--color-text-subtle)' }}> · {new Date(v.releasedAt).toLocaleDateString()}</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsTab;
